<template>
  <div class="game-page-wrapper">
    <Transition name="game-page">
      <MGameHeader v-if="!fullscreenMode" :game-id="gameId" />
    </Transition>
    <div class="game-page" data-t="m-game-page">
      <div class="content" :class="{ fullscreen: fullscreenMode }">
        <div v-if="isLoading" class="loading-container">
          <StIcon name="loading" size="32" data-t="loading-spinner" />
          <span class="loading-message">{{ t('casino.loadingDotsFull') }}</span>
        </div>
        <div
          v-if="fullscreenMode"
          class="fullscreen-button"
          data-t="close-fullscreen-button"
          @click="toggleFullscreenMode"
        >
          <StIcon name="minimize" size="20" />
        </div>
        <iframe
          v-if="frameUrl"
          ref="iframe"
          :key="frameUrl"
          :src="frameUrl"
          allow="payment"
          data-t="m-game-iframe"
          height="100%"
          loading="eager"
          width="100%"
          class="disable-screenshot"
          @load="handleFrameLoad"
        />
      </div>
    </div>
    <Transition name="game-page">
      <MGameFooter
        v-if="!fullscreenMode"
        :game-id="gameId"
        :has-demo-mode="hasDemo"
        @toggle-fullscreen="toggleFullscreenMode"
      />
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import { useScreenOrientation } from '@st/use/composables'
import MGameFooter from './parts/MGameFooter.vue'
import MGameHeader from './parts/MGameHeader.vue'
import { useGamePage } from '../../composables/useGamePage'

const iframe = useTemplateRef<HTMLIFrameElement>('iframe')

const { gameId } = defineProps<{
  gameId: number
}>()

const { isLoading, frameUrl, hasDemo, handleFrameLoad } = useGamePage({
  gameId: () => gameId,
})

const { t } = useI18n()
const { orientation } = useScreenOrientation()

const fullscreenMode = ref(false)
function toggleFullscreenMode() {
  fullscreenMode.value = !fullscreenMode.value
}

watch(
  () => orientation.value,
  (newValue, oldValue) => {
    if (
      newValue === 'landscape-primary' ||
      newValue === 'landscape-secondary'
    ) {
      fullscreenMode.value = true
    }

    if (
      (newValue === 'portrait-primary' || newValue === 'portrait-secondary') &&
      (oldValue === 'landscape-primary' || oldValue === 'landscape-secondary')
    ) {
      fullscreenMode.value = false
    }
  },
)
</script>

<style scoped>
.game-page-wrapper {
  display: flex;
  flex-direction: column;
  height: 100dvh;
}

.game-page {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: var(--spacing-100);

  height: 100%;
}

.content {
  display: flex;
  flex-grow: 1;
  /* stylelint-disable-next-line */
  padding-bottom: 56px;
  /* stylelint-disable-next-line */
  background: #000;
  border-radius: var(--border-radius-150);

  &.fullscreen {
    padding: 0;
  }
}

.fullscreen-button {
  position: absolute;
  z-index: 2;
  top: 50%;
  left: 0;

  display: inline-flex;
  gap: 0;
  align-items: flex-start;

  padding: var(--spacing-100, 8px) var(--spacing-100, 8px)
    var(--spacing-100, 8px) var(--spacing-050, 4px);

  background: rgb(0 0 0 / 24%);
  backdrop-filter: blur(4px);
  border-top: var(--border-width-boldest, 2px) solid rgb(0 0 0 / 24%);
  border-right: var(--border-width-boldest, 2px) solid rgb(0 0 0 / 24%);
  border-bottom: var(--border-width-boldest, 2px) solid rgb(0 0 0 / 24%);
  border-radius: var(--border-radius-0, 0) var(--border-radius-full, 999px)
    var(--border-radius-full, 999px) var(--border-radius-0, 0);
}

iframe {
  border: 0;
}

.loading-container {
  position: absolute;
  z-index: 2;
  inset: 0;

  display: flex;
  flex-direction: column;
  gap: var(--spacing-100);
  align-items: center;
  justify-content: center;
}

.loading-message {
  font: var(--mobile-text-content-regular);
  color: var(--text-secondary);
}

.game-page-enter-active,
.game-page-leave-active {
  transition:
    opacity 0.5s,
    transform 0.5s;
}

.game-page-enter-from,
.game-page-leave-to {
  transform: translateY(40px);
  opacity: 0;
}
</style>
